/**
 * SsoCallback.js (Lane 1252)
 *
 * Landing page for the Microsoft (Entra) SSO flow. The backend callback
 * redirects here with a single-use short-lived ?code=; this page exchanges
 * it at POST /api/sso/exchange for the standard {token, user} login
 * response and stores it through the same AuthContext path password login
 * uses. The login token itself never appears in any URL.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';

function SsoCallback() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  // React 18 StrictMode mounts effects twice in dev; the code is single-use
  // server-side, so the second run must not fire a second exchange.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      navigate('/login?sso_error=auth_failed', { replace: true });
      return;
    }

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/sso/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = await response.json();
        if (!response.ok || !data.token || !data.user) {
          setFailed(true);
          navigate('/login?sso_error=expired', { replace: true });
          return;
        }
        loginWithToken(data.token, data.user);
        navigate('/', { replace: true });
      } catch (err) {
        setFailed(true);
        navigate('/login?sso_error=unavailable', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#8A9BAE',
      fontSize: 'var(--font-size-md)',
    }}>
      {failed ? 'Returning to sign-in…' : 'Completing sign-in…'}
    </div>
  );
}

export default SsoCallback;
